/* eslint-disable @typescript-eslint/naming-convention */
// types
import {
    DELIVERY_SPEED,
    MROChildCart,
    DELIVERY_SPEED_STRATEGY,
    Option,
    DeliverySpeedDetail,
} from "@swiggy-private/cart-types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { ReactNode } from "react";

// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { getOptionStyles, getSlaText } from "@swiggy-private/cart-ui";
import { format, replaceText } from "@swiggy-private/core-ui";
import { DELIVERY_METHOD_LABEL, DELIVERY_TYPE_ADD_ADDRESS } from "@cart-client/constants/CartDeliverySpeed";

export const isSelfDeliveryRx = (child: MROChildCart): boolean => {
    // self delivery check
    return (child?.configurations?.self_delivery_v2 || child?.isRestaurantSelfDeliveryV2Enabled) ?? false;
};

export const getSelectedOptionFromWidgetOptions = (
    options?: Option[],
    selectedSlaOption?: DeliverySpeedDetail,
): Option | undefined => {
    return options?.find((option) => option.option === selectedSlaOption?.type);
};

export const getChildAddress = (child: MROChildCart) => {
    const addresses = child.addresses ?? [];
    if (isEmpty(addresses)) {
        return undefined;
    }
    return addresses.find((address) => address.selected) ?? addresses[0];
};

/**
 * No address then disabled for all RXs
 */
export const isChildDeliverySpeedDisabled = (child: MROChildCart): boolean => {
    const address = getChildAddress(child);
    if (isEmpty(address)) {
        return true;
    }
    if (isSelfDeliveryRx(child)) {
        return false;
    }
    const slaOptions = address?.sla_options ?? [];
    if (isEmpty(slaOptions)) {
        return true;
    }
    return !slaOptions.some((option) => option?.strategy?.toUpperCase() === DELIVERY_SPEED_STRATEGY.VISIBLE);
};

export const getMRODeliveryMethodLabel = (
    child: MROChildCart,
    isDeliverySpeedDisabled: boolean,
    isSelfDelivery: boolean,
    selectedOptionHeading?: string,
): string => {
    if (isDeliverySpeedDisabled) {
        return DELIVERY_TYPE_ADD_ADDRESS;
    }
    if (isSelfDelivery) {
        return child.self_delivery_presentation_info?.title || "";
    }
    const heading = selectedOptionHeading || "";
    return heading ? format(DELIVERY_METHOD_LABEL, heading) : "";
};

export interface MRORestaurantDeliveryRowPresentation {
    isDeliverySpeedDisabled: boolean;
    isSelfDelivery: boolean;
    deliveryMethodLabel: string;
    deliveryIconAsHeading: ReactNode;
    showBoltImage: boolean;
    deliveryTimeRange: string;
    selectedDeliveryType?: string;
    isExpressDelivery: boolean;
    highlightColour?: string;
    deliveryTimeColor: keyof IColors;
}

export const getMRORestaurantDeliveryRowPresentation = (
    child: MROChildCart,
    selectedSlaOption?: DeliverySpeedDetail,
    deliverySpeedWidgetInfoV2?: { options?: Option[] } | null,
): MRORestaurantDeliveryRowPresentation => {
    const isDeliverySpeedDisabled = isChildDeliverySpeedDisabled(child);
    const isSelfDelivery = isSelfDeliveryRx(child);
    const selectedDeliveryType = selectedSlaOption?.type;

    let selectedOption: Option | undefined;
    let deliveryMethodLabel = "";
    let deliveryIconAsHeading: ReactNode = null;
    let showBoltImage = false;
    let deliveryTimeRange = "";
    let isExpressDelivery = false;
    let highlightColour: string | undefined;
    let deliveryTimeColor: keyof IColors = "text_High_Emphasis";

    if (isDeliverySpeedDisabled) {
        deliveryMethodLabel = getMRODeliveryMethodLabel(child, true, isSelfDelivery);
        deliveryTimeColor = "text_disabled";
    } else {
        selectedOption = getSelectedOptionFromWidgetOptions(deliverySpeedWidgetInfoV2?.options, selectedSlaOption);
        deliveryMethodLabel = getMRODeliveryMethodLabel(child, false, isSelfDelivery, selectedOption?.heading);
        deliveryTimeRange = getSlaText(selectedOption?.slaData ?? selectedSlaOption);
        isExpressDelivery = selectedDeliveryType?.toUpperCase() === DELIVERY_SPEED.EXPRESS;
        highlightColour = selectedOption?.meta?.highlightColor;

        if (!isSelfDelivery && child?.is_bolt_cart) {
            const deliverySpeedWithIcon = selectedOption?.deliverySpeedWithIcon || "";
            if (!isEmpty(deliverySpeedWithIcon)) {
                deliveryIconAsHeading = replaceText(deliverySpeedWithIcon, "", true);
                showBoltImage = !isEmpty(deliveryIconAsHeading);
            }
        }

        if (isExpressDelivery || showBoltImage) {
            deliveryTimeColor = getOptionStyles(true, false, highlightColour).slaColor;
        }
    }

    return {
        isDeliverySpeedDisabled,
        isSelfDelivery,
        deliveryMethodLabel,
        deliveryIconAsHeading,
        showBoltImage,
        deliveryTimeRange,
        selectedDeliveryType,
        isExpressDelivery,
        highlightColour,
        deliveryTimeColor,
    };
};
